/**
 * What day it is, in the household's own calendar. Computed for arithmetic, never for a reader.
 *
 * ==========================================================================================
 * MOVED HERE FROM `apps/app/src/lib/household-day.ts` — FEATURE 024
 * ==========================================================================================
 *
 * The API needed it. Every server-side "today" had been the UTC day (`toIsoDay(clock.now())`), which
 * is right for an `effective_from` and wrong for a screen whose whole subject is WHICH SIDE OF TODAY a
 * payment falls on: at 00:30 in Amsterdam on the 5th, the UTC day is still the 4th, so the 5th's debits
 * would read as tomorrow's. One implementation existed, so it was promoted rather than copied — the
 * app's module re-exports this one, and `machine-dates.test.ts` guards this file now.
 *
 * ==========================================================================================
 * WHY THIS IS ITS OWN MODULE — FEATURE 021
 * ==========================================================================================
 *
 * It lived inside `freshness-panel.tsx`, which is also the component that renders "Updated 3 days
 * ago" — so one file held both a machine date and a translated sentence. That is fine to read and
 * impossible to GUARD: `machine-dates.test.ts` asserts that a module computing a machine day imports
 * no language resolver, and the panel legitimately imports one for its labels.
 *
 * Splitting them makes the rule expressible at the only granularity a static check has. The panel
 * translates; this file counts.
 *
 * ==========================================================================================
 * `en-CA` IS NOT DISPLAY FORMATTING, AND MUST NOT FOLLOW THE READER'S LANGUAGE
 * ==========================================================================================
 *
 * It renders ISO order, which is the cheapest `YYYY-MM-DD` out of `Intl` without assembling parts by
 * hand. Nobody reads the result: it is fed straight to `Date.parse` below. This is the THIRD such
 * site in the repository — the two others are the Finnhub and CBS jobs — and the one a sweep finds
 * first, because it sits under `components/dashboard/` where the other two are nowhere near.
 *
 * Routing it through `@gladis/ui/i18n` breaks the arithmetic SILENTLY: nothing throws, `Date.parse`
 * returns NaN, and every module reads "Updated today" for ever.
 */

/** The household's timezone, which is what a "day" is counted in. EUR only, one family, one zone. */
export const HOUSEHOLD_ZONE = "Europe/Amsterdam";

/** `YYYY-MM-DD` for an instant, as the household's calendar sees it. */
export function localDay(at: Date): string {
  return at.toLocaleDateString("en-CA", { timeZone: HOUSEHOLD_ZONE });
}

/** Whole days between two local days, by calendar rather than by elapsed hours. */
export function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  return Math.round((end - start) / 86_400_000);
}
